import {
  Button,
  DocumentList,
  ShipmentView,
  SignaturePad,
  type SignaturePadHandle,
  type SignaturePadState,
  Timeline,
} from '@freightapi/ui'
import { demoDocuments } from '@freightapi/ui/fixtures/documents'
import { demoTimeline } from '@freightapi/ui/fixtures/events'
import { demoRemarks, demoShipment } from '@freightapi/ui/fixtures/shipment'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { type ReactNode, useRef, useState } from 'react'
import { useSignI18n } from '../lib/locale'

/**
 * The gallery that replaces Storybook: every shared component on one 390px-wide page with the
 * fixtures of `DEMO-0002`, so a change to `packages/ui` is reviewed by looking at it.
 *
 * Development only. A production build still contains the route module, but it renders the
 * not-found screen: fixtures on a public signing domain would read as real shipment data.
 */
export const Route = createFileRoute('/dev/fixtures')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound()
  },
  component: FixturesPage,
})

function FixturesPage() {
  const { t } = useSignI18n()
  const pad = useRef<SignaturePadHandle>(null)
  const [state, setState] = useState<SignaturePadState>({ isEmpty: true, strokeCount: 0 })
  const [exported, setExported] = useState<string>(t('fixtures.signature.empty'))

  function exportPng() {
    const png = pad.current?.toPNG()
    setExported(
      png === null || png === undefined
        ? t('fixtures.signature.empty')
        : t('fixtures.signature.exported', {
            width: png.width,
            height: png.height,
            bytes: png.bytes,
          }),
    )
  }

  return (
    <div className="flex flex-col gap-8">
      <h1 className="font-semibold text-display text-ink">{t('fixtures.title')}</h1>

      <Section title={t('fixtures.shipment')}>
        <ShipmentView
          shipment={demoShipment}
          status="in_transit"
          version={2}
          viewer="console"
          remarks={demoRemarks}
        />
      </Section>

      <Section title={t('fixtures.timeline')}>
        <Timeline entries={demoTimeline} />
      </Section>

      <Section title={t('fixtures.documents')}>
        <DocumentList documents={demoDocuments} />
      </Section>

      <Section title={t('fixtures.signature')}>
        <SignaturePad ref={pad} label={t('fixtures.signature')} onChange={setState} />
        <div className="flex items-center justify-between gap-3">
          <p data-testid="signature-export" className="text-caption text-muted">
            {exported}
          </p>
          <Button onClick={exportPng} disabled={state.isEmpty}>
            {t('fixtures.signature.export')}
          </Button>
        </div>
      </Section>
    </div>
  )
}

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-semibold text-muted text-title">{title}</h2>
      {children}
    </section>
  )
}
